import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import PostpartumTimeCell from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/PostpartumTable/PostpartumTimeCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getNewbornLocationRowValues } from "@/encounters/components/EncounterLabor/utils/postpartum";

interface NewbornLocationRowProps {
  time: string;
  observation: Observation | undefined;
  showNumbersOnly?: boolean;
  isViewOnly: boolean;
  showPreviousVersions?: boolean;
  onRefresh?: () => Promise<void>;
}

interface LocationItem {
  label: string;
  value: string | undefined;
}

export default function NewbornLocationRow({
  time,
  observation,
  showNumbersOnly,
  isViewOnly,
  showPreviousVersions = true,
  onRefresh,
}: NewbornLocationRowProps) {
  const { location, inWater, notes, hasAnswers, hasNotes } = useMemo(
    () =>
      observation
        ? getNewbornLocationRowValues(observation)
        : {
            location: undefined,
            inWater: undefined,
            notes: undefined,
            hasAnswers: false,
            hasNotes: false,
          },
    [observation],
  );

  const locationItems = useMemo<LocationItem[]>(
    () => [
      {
        label: "Location",
        value: location,
      },
      {
        label: "In Water",
        value: inWater,
      },
    ],
    [location, inWater],
  );

  if (!observation) return null;

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <PostpartumTimeCell
          time={time}
          rowSpan={1}
          onRefresh={onRefresh}
          isViewOnly={isViewOnly}
          observation={observation}
        />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Location:</TextWrapper>
            <List fz="sm" withPadding>
              {locationItems.map(
                (item) =>
                  item.value && (
                    <List.Item key={`${item.label}-${item.value}`}>
                      <Group gap="xs">
                        <TextWrapper size="sm" variant="bold">
                          {item.label}:
                        </TextWrapper>
                        <TextWrapper size="sm">{item.value}</TextWrapper>
                      </Group>
                    </List.Item>
                  ),
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Location Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={NewbornLocationRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
    </Table.Tr>
  );
}
